import { ServiceFieldType, ServicesProperty } from '@onlog-public/additional-services-types';
import { AdditionServicesOptionsState } from '@pages/AdditionalServices/containers/additionServicesOptionsContext';
import optionsLoader from '@pages/AdditionalServices/containers/services/optionsLoader';
import { OptionsCache } from '@pages/AdditionalServices/containers/services/optionsLoader/types';
import {
  AServiceCache,
  AServicePropertyCache,
  AServicePropertyFilterAndOrder,
  AdditionServicesState,
} from '@pages/AdditionalServices/containers/types';

import { ServiceData, SetUpServiceData } from '../../../basket/types';
import { serviceRestoreStateGeneratorProcessor } from '../types';

/**
 * ServicePropertyCacheProcessor формирует кэш базовых данных для свойств услуг
 */
export class ServicePropertyCacheProcessor implements serviceRestoreStateGeneratorProcessor {
  /**
   * restore выполняет восстановление стейтов. Возвращает все стейты.
   * @param _
   * @param __
   * @param additionalServiceState
   * @param optionsState
   * @param serviceCacheState
   * @param servicePropertyCacheState
   * @param servicePropertyFilterAndOrderState
   */
  async restore(
    _: SetUpServiceData,
    __: (SetUpServiceData | ServiceData)[],
    additionalServiceState: AdditionServicesState,
    optionsState: AdditionServicesOptionsState,
    serviceCacheState: AServiceCache,
    servicePropertyCacheState: AServicePropertyCache,
    servicePropertyFilterAndOrderState: AServicePropertyFilterAndOrder
  ) {
    const result: { [K in ServiceFieldType]: number[] } = {
      number: [],
      switch: [],
      hidden: [],
      tax: [],
      location: [],
      handbook: [],
      currency: [],
      consulting_contractor: [],
      inspection_contractor: [],
      customs_contractor: [],
      certification_contractor: [],
      insurance_contractor: [],
      package: [],
      product: [],
    };

    /**
     * Получаем текущий кэш и формируем мапу всех свойств из переданных
     * данных. Свойства нужны для того, чтобы понять, какой кэш нужен.
     */
    const cache = servicePropertyCacheState.Cache;
    const allProperties: { [T in string]: ServicesProperty } = {};

    const props = [
      ...additionalServiceState.PropertiesToDisplay,
      ...Object.values(additionalServiceState.VariantProperties).flat(1),
    ];

    props.map((p) => (allProperties[p.id] = p));

    /**
     * Выгружаем все не загруженные идентификаторы из свойств типа справочники.
     * По итогу получаем в результатах не уникальные идентификаторы, которые
     * надо загрузить.
     */
    Object.values(additionalServiceState.PropertyValues).map((props) => {
      Object.keys(props).map((propertyID) => {
        const property = allProperties[propertyID];
        if (!property) {
          return;
        }

        if (['switch', 'number', 'hidden'].includes(property.type)) {
          return;
        }

        let values = props[propertyID];
        if (property.type === 'handbook') {
          values = [property.handbook_id];
        }

        const currentCache: OptionsCache<any> = cache[property.type] ?? {
          isPartialLoading: true,
          cache: {},
        };

        return (result[property.type] = [
          ...(result[property.type] ?? []),
          ...values.filter((id) => !currentCache.cache[id]),
        ]);
      });
    });

    /**
     * Получаем только уникальные идентификаторы справочников, которые надо
     * загрузить. Так же проверяем, что вообще данные для загрузки есть.
     */
    Object.keys(result).map((type: ServiceFieldType) => {
      result[type] = result[type].filter((d, i, data) => data.indexOf(d) === i);
    });

    const resultData = await Promise.all(
      Object.keys(result).map(async (type: ServiceFieldType) => {
        const keys = result[type].map((id) => String(id));

        return {
          type: type,
          items: await optionsLoader().LoadOptionsCache(type, keys),
        };
      })
    );

    const state = servicePropertyCacheState;
    const newCache = { ...state.Cache };

    resultData.map((r) => {
      newCache[r.type] = {
        isPartialLoading: true,
        cache: {
          ...(newCache[r.type]?.cache ?? {}),
          ...r.items.cache,
        },
      };
    });

    return {
      isValid: true,
      additionalServiceState,
      optionsState,
      serviceCacheState,
      servicePropertyCacheState: {
        ...state,
        Cache: newCache,
      },
      servicePropertyFilterAndOrderState,
    };
  }
}
